import { useEffect, useMemo, useRef } from "react";
import Clipboard from "clipboard";
import Embed from "util/embed-code";
import { H4 } from "visual-components/util/texts";

/**
 * Full-page HTML snippet panel for the legacy "paste this whole document" install path.
 * Uses the legacy Clipboard library on the anchor element via a ref.
 *
 * @param {Object} props
 * @param {Object} props.currentCampaign - Campaign being edited (uid, name, desktopElements, etc.).
 * @param {Object} props.currentBrand - Brand creating the campaign (analytics/adRoll IDs).
 * @returns {import("react").ReactElement}
 */
export function FullPageSnippetPanel(props) {
  const { currentCampaign, currentBrand } = props;

  const copyRef = useRef(null);

  const embed = useMemo(
    () =>
      Embed({
        description: currentCampaign.description,
        name: currentCampaign.name,
        twitterShareMessage: currentCampaign.twitterShareMessage,
        facebookShareImage: currentCampaign.facebookShareImage,
        mainImage: currentCampaign.desktopElements.mainImage,
        staticPath: `//landing.dojomojo.com/landing/campaign/${currentCampaign.uid}`,
        ga: currentBrand.googleAnalyticsCode,
        adRollId: currentBrand.adRollId,
        adRollPixelId: currentBrand.adRollPixelId,
        id: currentCampaign.uid,
      }),
    [currentCampaign, currentBrand],
  );

  useEffect(() => {
    if (!copyRef.current) return undefined;
    const clipboard = new Clipboard(copyRef.current);
    return () => clipboard.destroy();
  }, []);

  /**
   * Toast notification after the legacy Clipboard copy succeeds.
   * @returns {void}
   */
  const handleCopied = () => {
    toastr.success("embed code copied to clipboard", null, { timeOut: 1000 });
  };

  return (
    <div className="row text-left" style={{ marginTop: "16px" }}>
      <div className="col-sm-12">
        <ol>
          <li>
            <H4 multiline>
              Share{" "}
              <a
                ref={copyRef}
                onClick={handleCopied}
                className="copy-embed"
                data-clipboard-text={embed}
              >
                {"this snippet of code"}
              </a>{" "}
              with your developer and have them embed it in your website.
            </H4>
          </li>
          <li>
            <H4 multiline>
              Paste the web address where your giveaway landing page will live below and click
              Activate.
            </H4>
          </li>
        </ol>
      </div>
    </div>
  );
}
